import React, { type JSX, memo, useState, useMemo, useEffect, useId } from 'react';
import { Tree, type TreeProps } from 'primereact/tree';
import type { TreeNode } from 'primereact/treenode';
import styles from './NavLink.module.scss';
import { Icon } from '../Icon';
import { Status } from '../Status';
import { Button } from '../Button';

/* ----------------------------------
 * Types aligned with Stories
 * ---------------------------------- */
export interface NavLinkType {
  key: string;
  label: string;
  data?: {
    icon?: string;
    count?: string;
    status?: string;
    description?: string;
    state?: Array<string>;
    action?: {
      type: string;
      size: string;
      icon: string;
      filled?: boolean;
      onClick?: () => void;
    };
  };
  children?: NavLinkType[];
  leaf?: boolean;
}

export interface NavLinkProps {
  items: NavLinkType[];
  state?: string;
  navigation?: boolean;
  showIcon?: boolean;
  showSubNav?: boolean;
  showNotification?: boolean;
  togglerIcon?: React.ReactNode;
  treeProps?: Partial<TreeProps>;

  onItemMouseEnter?: (
    item: NavLinkType,
    target: HTMLDivElement,
    event: React.MouseEvent<HTMLDivElement>,
  ) => void;

  onItemMouseLeave?: (
    item: NavLinkType,
    event: React.MouseEvent<HTMLDivElement>,
  ) => void;
}

type NavTogglerProps = {
  prefixInstance?: string;
};

/**
 * Custom NavLink component using PrimeReact TreeModule.
 * It supports hierarchical navigation with optional icons, status indicators,
 * notification tags, custom toggle icons, and row hover events.
 */
export const NavLink: React.FC<NavLinkProps> = memo(
  ({
    items,
    state = 'default',
    navigation = false,
    showIcon = true,
    showSubNav = true,
    showNotification = true,
    togglerIcon,
    treeProps,
    onItemMouseEnter,
    onItemMouseLeave,
  }) => {
    const dragDropId = useId();
    const dragdropScope = useMemo(() => `navlink-${dragDropId.replace(/:/g, '')}`, [dragDropId]);

    const [expandedKeys, setExpandedKeys] = useState<Record<string, boolean>>({});
    const [selectedKey, setSelectedKey] = useState<string | null>(null);

    useEffect(() => {
      if (state === 'expanded') {
        const allKeys: Record<string, boolean> = {};

        const expandNode = (nodes: NavLinkType[]) => {
          for (const node of nodes) {
            if (node.children && node.children.length > 0) {
              allKeys[node.key] = true;
              expandNode(node.children);
            }
          }
        };

        expandNode(items);
        setExpandedKeys(allKeys);
      } else {
        setExpandedKeys({});
      }

      setSelectedKey(null);
    }, [state, items]);

    useEffect(() => {
      const treeRoot = document.querySelector(
        `[data-testid="navlink-component"] [data-pc-name="tree"]`,
      );

      if (!treeRoot) return;

      const applyLevelClasses = (ul: Element, level: number) => {
        ul.classList.remove('level-one', 'level-two', 'level-three', 'level-four');
        ul.classList.add(`level-${level}`);

        Array.from(ul.children).forEach((li) => {
          const childUl = li.querySelector(':scope > ul');
          if (childUl) {
            applyLevelClasses(childUl, level + 1);
          }
        });
      };

      const rootUl = treeRoot.querySelector(':scope > ul');
      if (rootUl) {
        applyLevelClasses(rootUl, 1);
      }
    }, [expandedKeys, items]);

    const iconModules = useMemo(() => {
      return import.meta.glob(
        '../../../assets/Icons/Base/*_filled.{png,jpg,jpeg,svg}',
        { eager: true, import: 'default' }
      );
    }, []);

    const getFilledIconPath = (iconName: string): string | null => {
      const expectedFilename = `${iconName}_filled.svg`;

      for (const [path, mod] of Object.entries(iconModules)) {
        const filename = path.split('/').pop();
        if (filename === expectedFilename) return mod as string;
      }
      return null;
    };

    const getStateClass = (value: string) => {
      switch (value) {
        case 'hover':
          return styles.hover;
        case 'active':
          return styles.activepressed;
        case 'disabled':
          return styles.disabled;
        default:
          return styles.default;
      }
    };

    const toTreeNodes = (data: NavLinkType[]): TreeNode[] =>
      data.map((item) => ({
        key: item.key,
        label: item.label,
        data: item.data,
        leaf: item.leaf,
        children: item.children ? toTreeNodes(item.children) : undefined,
      }));

    const toNavLinkItem = (node: TreeNode): NavLinkType => ({
      key: String(node.key ?? ''),
      label: String(node.label ?? ''),
      data: node.data,
      leaf: node.leaf,
      children: node.children ? node.children.map(toNavLinkItem) : undefined,
    });

    const [treeNodes, setTreeNodes] = useState<TreeNode[]>(() => toTreeNodes(items));

    useEffect(() => {
      setTreeNodes(toTreeNodes(items));
    }, [items]);

    const normalizeNodes = (nodes: TreeNode[]): TreeNode[] =>
      nodes.map((node) => ({
        ...node,
        data: node.data ?? undefined,
        children: node.children ? normalizeNodes(node.children) : undefined,
      }));

    const collectNodeKeys = (nodes: TreeNode[]): Set<string> => {
      const keys = new Set<string>();

      const visit = (currentNodes: TreeNode[]) => {
        currentNodes.forEach((node) => {
          if (typeof node.key === 'string') {
            keys.add(node.key);
          }

          if (node.children?.length) {
            visit(node.children);
          }
        });
      };

      visit(nodes);

      return keys;
    };

    const hasExpandedChild = (
      node: TreeNode,
      currentExpandedKeys: Record<string, boolean>,
    ): boolean =>
      !!node.children?.some(
        (child: TreeNode) =>
          currentExpandedKeys[String(child.key)] || hasExpandedChild(child, currentExpandedKeys),
      );

    const hasSelectedChild = (node: TreeNode, currentSelectedKey: string | null): boolean =>
      !!currentSelectedKey &&
      !!node.children?.some(
        (child: TreeNode) =>
          String(child.key) === currentSelectedKey || hasSelectedChild(child, currentSelectedKey),
      );

    /* ----------------------------------
     * Node Template
     * ---------------------------------- */
    const nodeTemplate = (node: TreeNode): JSX.Element => {
      const nodeKey = String(node.key ?? '');
      const nodeState = node.data?.state || state;
      const isDisabled = nodeState === 'disabled';
      const isLeafNode = !node.children || node.children.length === 0;

      const isExpanded = Boolean(expandedKeys[nodeKey]);
      const isChildExpanded = hasExpandedChild(node, expandedKeys);
      const isChildSelected = hasSelectedChild(node, selectedKey);

      const hasExpandedChildWithChildren = node.children?.some(
        (child: TreeNode) =>
          expandedKeys[String(child.key)] && child.children && child.children.length > 0,
      );

      const isBgActive = isExpanded && (!isChildExpanded || hasExpandedChildWithChildren);
      const isSelectedActive = isExpanded && isChildSelected;

      const shouldUseFilled = isBgActive;
      const filledIconPath =
        shouldUseFilled && node.data?.icon ? getFilledIconPath(node.data.icon) : null;

      const navItem = toNavLinkItem(node);

      return (
        <div
          className={`${styles.navLink} ${getStateClass(nodeState)} ${isBgActive ? styles.activeBg : ''
            } ${isSelectedActive ? styles.activeSelectedBg : ''}`}
          data-navlink-row="true"
          data-navlink-key={nodeKey}
          onMouseEnter={(event) => {
            if (isDisabled) {
              return;
            }

            onItemMouseEnter?.(navItem, event.currentTarget, event);
          }}
          onMouseLeave={(event) => {
            if (isDisabled) {
              return;
            }

            onItemMouseLeave?.(navItem, event);
          }}
        >
          {/* PREFIX */}
          <div className={styles.prefix}>
            {!isLeafNode &&
              showIcon &&
              node.data?.icon &&
              (shouldUseFilled && filledIconPath ? (
                <img src={filledIconPath} alt={node.data.icon} width={24} height={24} />
              ) : (
                <Icon
                  name={node.data.icon}
                  variant="Outline"
                  type="Base Icon"
                  size="Large"
                  shape="Square"
                />
              ))}

            {node.data?.status && (
              <div className={styles.navLinkStatus}>
                <Status
                  size="Regular"
                  icon={node.data.status}
                  label={false}
                  iconType="Nav Status"
                />
              </div>
            )}
          </div>

          {/* TEXT */}
          <div className={styles.navheading}>
            <span className={styles.label}>{node.label}</span>

            {node.data?.description && (
              <span className={styles.description}>{node.data.description}</span>
            )}
          </div>

          {/* RIGHT */}
          <div className={styles.rightContent}>
            {!isLeafNode && showNotification && node.data?.count && (
              <span className={styles.count}>{node.data.count}</span>
            )}

            {node.data?.action && (
              <Button
                buttonPrefix
                buttonType={node.data.action.type}
                buttonClass={node.data.action.filled ? styles.iconFilled : ''}
                variant={node.data.action.filled ? 'Filled' : 'Outline'}
                classType="Standard"
                onClick={(e) => {
                  e.stopPropagation();
                  node.data?.action?.onClick?.();
                }}
                prefixInstance={node.data.action.icon}
                size={node.data.action.size}
                state="Default"
                theme="Rectangular"
              />
            )}

            {!isLeafNode && showSubNav && !node.data?.action && togglerIcon && (
              <span
                className={`${styles.buttonWrapper} ${isDisabled ? styles.disabledChevron : ''}`}
                role="button"
                tabIndex={isDisabled ? -1 : 0}
                aria-disabled={isDisabled}
                onClick={(e) => {
                  if (isDisabled) return;

                  e.stopPropagation();
                  e.preventDefault();

                  setExpandedKeys((prev) => {
                    const next = { ...prev };

                    if (next[nodeKey]) {
                      delete next[nodeKey];
                    } else {
                      next[nodeKey] = true;
                    }

                    return next;
                  });
                }}
                onKeyDown={(e) => {
                  if (isDisabled) return;

                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                    e.preventDefault();

                    setExpandedKeys((prev) => {
                      const next = { ...prev };

                      if (next[nodeKey]) {
                        delete next[nodeKey];
                      } else {
                        next[nodeKey] = true;
                      }

                      return next;
                    });
                  }
                }}
              >
                {React.cloneElement(togglerIcon as React.ReactElement<NavTogglerProps>, {
                  prefixInstance: expandedKeys[nodeKey] ? 'Chevron Up' : 'Chevron Down',
                })}
              </span>
            )}
          </div>
        </div>
      );
    };

    return (
      <div
        className={styles.navContainer}
        data-testid="navlink-component"
        data-dragdrop-enabled={state === 'dragdrop'}
      >
        {navigation && <div className={styles.navHeader}>{items[0]?.label}</div>}

        <Tree
          {...treeProps}
          value={treeProps?.value ?? treeNodes}
          nodeTemplate={treeProps?.nodeTemplate ?? nodeTemplate}
          togglerTemplate={treeProps?.togglerTemplate ?? (() => null)}
          selectionMode={treeProps?.selectionMode ?? 'single'}
          selectionKeys={treeProps?.selectionKeys ?? selectedKey}
          onSelectionChange={
            treeProps?.onSelectionChange ?? ((e) => setSelectedKey(e.value as string))
          }
          expandedKeys={treeProps?.expandedKeys ?? expandedKeys}
          onToggle={treeProps?.onToggle ?? ((e) => setExpandedKeys(e.value))}
          className={[styles.primeTree, treeProps?.className].filter(Boolean).join(' ')}
          dragdropScope={(treeProps?.dragdropScope ?? state === 'dragdrop') ? dragdropScope : undefined}
          onDragDrop={
            (treeProps?.onDragDrop ?? state === 'dragdrop')
              ? (e) => {
                const droppedNodes = Array.isArray(e.value) ? e.value : treeNodes;
                const normalized = normalizeNodes(droppedNodes);
                const currentKeys = collectNodeKeys(normalized);

                setTreeNodes(normalized);

                setExpandedKeys((prev) => {
                  const next: Record<string, boolean> = {};

                  Object.keys(prev).forEach((key) => {
                    if (currentKeys.has(key)) {
                      next[key] = true;
                    }
                  });

                  return next;
                });

                setSelectedKey((prev) => (prev && currentKeys.has(prev) ? prev : null));
              }
              : undefined
          }
        />
      </div>
    );
  },
);

NavLink.displayName = 'NavLink';